import { ShieldWarningIcon } from '@phosphor-icons/react';

import { Button } from '@/ui/button/Button';

import type { PendingApproval } from '../../stores/agent-session-store';

import { describeToolName } from './tool-metadata';

/**
 * The agent is blocked on this, so it is pinned above the composer rather than placed inline in
 * the thread: a card that scrolled away with the transcript would strand the turn behind
 * something the user can no longer see.
 *
 * Only one button grants. There is no "always allow" — approving already lasts the rest of the
 * session, and a second, broader-sounding option would suggest it doesn't.
 */
export function ApprovalCard({
  approval,
  onRespond,
}: {
  approval: PendingApproval;
  onRespond: (id: string, approved: boolean) => void;
}) {
  const { connector, tool } = describeToolName(approval.tool);

  return (
    <div className="border-t border-line bg-surface-2 px-6 py-3">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-warning-tint text-warning">
          <ShieldWarningIcon size={16} weight="fill" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-fg">
              {connector ? `Let the agent use ${connector}?` : 'Let the agent run this?'}
            </span>
            <span className="text-xs text-fg-secondary">
              It wants to run <code className="text-fg">{tool}</code>, which changes data outside this sandbox. Allowing
              it won&apos;t ask again for this tool in this session.
            </span>
          </div>

          <Arguments input={approval.input} />

          <div className="flex gap-2">
            <Button type="button" variant="primary" size={8} onClick={() => onRespond(approval.id, true)} autoFocus>
              Allow
            </Button>
            <Button type="button" variant="secondary" size={8} onClick={() => onRespond(approval.id, false)}>
              Don&apos;t allow
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The call's arguments, so the decision is informed rather than blind.
 *
 * Capped hard: a `create_playbook` body can be thousands of lines, and this sits above the
 * composer where it would push the whole thread off screen.
 */
function Arguments({ input }: { input: unknown }) {
  if (input === null || input === undefined) return null;

  let text: string;
  try {
    text = typeof input === 'string' ? input : JSON.stringify(input, null, 2);
  } catch {
    text = String(input);
  }
  if (!text || text === '{}') return null;

  const clipped = text.length > 1200 ? `${text.slice(0, 1200)}\n…` : text;

  return (
    <pre className="max-h-32 overflow-auto rounded-md bg-surface-3 px-2.5 py-2 font-mono text-2xs leading-4 text-fg-secondary">
      {clipped}
    </pre>
  );
}
